import { useSyncExternalStore } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { defineStoredString, type StoredValue } from '../../utils/storage';
import { markBoot } from '../../utils/bootTrace';
import { whenDatabaseSettled } from '../../db/openInFlight';

/**
 * Whether a newer build of the app is running behind the page, and how to get
 * into it.
 *
 * The service worker runs in 'autoUpdate' mode (see vite.config.ts): a new
 * build installs in the background and takes control at once, never waiting for
 * a signal from the page. `onNeedReload` then replaces the plugin's automatic
 * reload — which would drop whatever is being typed — with our own hand-over:
 * reload immediately if nobody has touched the app since it loaded, otherwise
 * mark the profile menu and let the user say when. Either way the page already
 * runs under the NEW worker, so the update is one reload away.
 *
 * The price of taking over early is that the open page outlives its own build:
 * the new worker's precache holds the new files only and the build empties
 * `dist`, so a lazy chunk this page has not loaded YET (a route nobody visited,
 * exceljs, react-pdf, konva) is gone from both. `installChunkGuard` turns that
 * into a reload rather than a broken screen.
 *
 * Module state, not a hook's state: `registerSW` may only run ONCE per page,
 * and both chromes (office and field) mount the button.
 */

/** How often a tab that stays open all day re-checks the server for a build. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
/**
 * A silent update reloads the page, which registers again — a failing
 * hand-over would be a reload loop. One silent update per tab per window, then
 * the button.
 */
const SILENT_UPDATE_COOLDOWN_MS = 2 * 60 * 1000;
const silentUpdateAt = defineStoredString({
    key: 'pwa_silent_update',
    area: 'session'
});
/** The same brake for the chunk guard, which reloads for its own reason. */
const CHUNK_RELOAD_COOLDOWN_MS = 2 * 60 * 1000;
const chunkReloadAt = defineStoredString({
    key: 'pwa_chunk_reload',
    area: 'session'
});

/**
 * How long a silent update waits for the local database to finish opening.
 * Past that the open is stuck, and the update is offered instead: the person
 * can then take it on purpose — it may be the build that fixes the hang.
 */
const SILENT_UPDATE_WAIT_MS = 30_000;

/** Whether the database open settled within `ms`. */
function databaseSettledWithin(ms: number): Promise<boolean> {
    return Promise.race([
        whenDatabaseSettled().then(() => true),
        new Promise<boolean>((resolve) => setTimeout(() => resolve(false), ms))
    ]);
}

let updateAvailable = false;
let nextVersion: string | null = null;
let registered = false;
let interacted = false;
/** When the chunk guard last started a reload; 0 while it has not. */
let chunkReloadStartedAt = 0;
let applyUpdate: () => void = () => {};
const listeners = new Set<() => void>();

function emit() {
    for (const listener of listeners) {
        listener();
    }
}

/**
 * Whether the new build may take over without asking: nothing typed or tapped
 * since this page loaded. Deliberately NOT a time window — precaching over a
 * field connection can take minutes, and an untouched app is as safe to reload
 * then as at second three.
 */
function mayUpdateSilently(): boolean {
    if (interacted) {
        return false;
    }
    return takeReloadSlot(silentUpdateAt, SILENT_UPDATE_COOLDOWN_MS);
}

/**
 * Whether this tab may reload itself for `at` — at most once per `cooldown`, so
 * a reload landing back on the same problem cannot become a loop. Says yes only
 * by WRITING the brake, so blocked storage (private mode) counts as no.
 */
function takeReloadSlot(
    at: StoredValue<string | null>,
    cooldown: number
): boolean {
    const last = Number(at.read() ?? 0);
    if (!(Date.now() - last > cooldown)) {
        return false;
    }
    return at.write(String(Date.now()));
}

/**
 * The version the SERVER is on, from the `version.json` the build writes (see
 * vite.config.ts). The waiting worker cannot be asked — it is the same `/sw.js`
 * URL either way. Never fatal: without it the button simply says that a new
 * version exists.
 */
async function readNextVersion(): Promise<string | null> {
    try {
        const response = await fetch('/version.json', { cache: 'no-store' });
        if (!response.ok) {
            return null;
        }
        const data: unknown = await response.json();
        const value = (data as { version?: unknown }).version;
        return typeof value === 'string' ? value : null;
    } catch {
        return null;
    }
}

/**
 * Ask the browser to re-fetch the service worker, so a device that never
 * reloads still learns about a new build. Skipped while offline or mid-install
 * — the check is a nicety, not something to retry.
 */
async function checkForUpdate(
    swUrl: string,
    registration: ServiceWorkerRegistration
) {
    if (registration.installing || !navigator.onLine) {
        return;
    }
    try {
        // `no-store`: bypass every cache and see what the server has now.
        const response = await fetch(swUrl, {
            cache: 'no-store',
            headers: { 'cache-control': 'no-cache' }
        });
        if (response.status === 200) {
            await registration.update();
        }
    } catch {
        // Server unreachable — the next check (or the next load) will do.
    }
}

/**
 * Reload when a lazily loaded chunk cannot be fetched — what an outdated page
 * looks like from the inside after an update: the file it asks for belongs to a
 * build that exists neither in the new worker's precache nor on the server. The
 * reload loses unsaved input in an open form, but the navigation that triggered
 * the import was leaving that form anyway, and the alternative is a screen that
 * stays broken until someone reloads by hand.
 *
 * `vite:preloadError` covers the preload of a route chunk; the rejection
 * handler covers the import itself failing, which browsers report with a
 * message no standard pins down — hence matching on its text.
 *
 * Once Vite's error is prevented, the `import()` RESOLVES — with `undefined`.
 * `React.lazy` then throws "Cannot read properties of undefined (reading
 * 'default')" before the reload lands; see `isReloadingForChunk`.
 */
function installChunkGuard() {
    const reload = () => {
        if (!takeReloadSlot(chunkReloadAt, CHUNK_RELOAD_COOLDOWN_MS)) {
            return false;
        }
        chunkReloadStartedAt = Date.now();
        window.location.reload();
        return true;
    };
    window.addEventListener('vite:preloadError', (event) => {
        // Without preventDefault Vite rethrows, which would flash the error at
        // the user in the moment before the reload.
        if (reload()) {
            event.preventDefault();
        }
    });
    window.addEventListener('unhandledrejection', (event) => {
        const message =
            event.reason instanceof Error ? event.reason.message : '';
        if (/dynamically imported module|module script failed/i.test(message)) {
            reload();
        }
    });
}

/**
 * How long a started reload is given to land. It can be cancelled — a form with
 * unsaved input asks before the page goes, and "stay" keeps it — so waiting has
 * to end somewhere.
 */
export const CHUNK_RELOAD_GRACE_MS = 10_000;

/**
 * Whether the chunk guard started a reload that may still land. Anything that
 * breaks until then is a consequence of the missing chunk, not a fault of its
 * own — the error boundary waits for the reload instead of reporting it.
 */
export function isReloadingForChunk(): boolean {
    return (
        chunkReloadStartedAt > 0 &&
        Date.now() - chunkReloadStartedAt < CHUNK_RELOAD_GRACE_MS
    );
}

/**
 * Register the service worker. Called from main.tsx at boot, so the app shell
 * precaches before a login — the button below sits in the app bar, which only
 * exists after one.
 */
export function startAppUpdates() {
    ensureRegistered();
}

function ensureRegistered() {
    if (registered) {
        return;
    }
    registered = true;
    installChunkGuard();
    // The first touch of any kind ends the "safe to reload" window. `capture`
    // and `once`: it must be seen even when a handler stops the event, and it
    // is a one-way switch.
    for (const event of ['pointerdown', 'keydown'] as const) {
        window.addEventListener(
            event,
            () => {
                interacted = true;
            },
            { capture: true, once: true }
        );
    }
    registerSW({
        immediate: true,
        /**
         * Fires once the new worker has ACTIVATED and this page is its client.
         * Supplying it keeps the plugin from reloading by itself, so the reload
         * becomes ours to time.
         */
        onNeedReload() {
            // Never in the middle of opening the local database: a reload
            // there leaves a connection behind that the next start hangs on
            // (db/openStall.ts). After a deploy this was the common case — the
            // new worker activates while a slow device is still opening.
            void databaseSettledWithin(SILENT_UPDATE_WAIT_MS).then(
                (settled) => {
                    if (settled && mayUpdateSilently()) {
                        markBoot('update:reload');
                        window.location.reload();
                        return;
                    }
                    updateAvailable = true;
                    emit();
                    void readNextVersion().then((version) => {
                        nextVersion = version;
                        emit();
                    });
                }
            );
        },
        onRegisteredSW(swUrl, registration) {
            if (!registration) {
                return;
            }
            // In the boot trace: a new build precaching while the app starts
            // is one suspect for a slow database open.
            registration.addEventListener('updatefound', () => {
                markBoot('update:found');
            });
            window.setInterval(() => {
                void checkForUpdate(swUrl, registration);
            }, UPDATE_CHECK_INTERVAL_MS);
            // Coming back to the tab is the moment a user is most likely to
            // accept an update, so look for one right then.
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    void checkForUpdate(swUrl, registration);
                }
            });
        }
    });
    // A plain reload IS the update: the new worker already controls this page,
    // and `registerSW`'s updater does nothing in autoUpdate mode — it only
    // sends the skip-waiting message that a WAITING worker needs.
    applyUpdate = () => {
        window.location.reload();
    };
}

function subscribe(listener: () => void) {
    ensureRegistered();
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function useAppUpdate() {
    // Two subscriptions rather than one object: a getSnapshot building an
    // object would hand back a new identity every render and loop.
    const available = useSyncExternalStore(
        subscribe,
        () => updateAvailable,
        () => false
    );
    const version = useSyncExternalStore(
        subscribe,
        () => nextVersion,
        () => null
    );
    return {
        updateAvailable: available,
        /** The version waiting to take over, once it is known. */
        newVersion: version,
        applyUpdate: () => applyUpdate()
    };
}
